import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { Content, Header, Link, Page, Progress, StatusError, StatusOK, StatusRunning, StatusWarning, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { QRCodeSVG } from 'qrcode.react';

const SLUG = 'github.com/project-slug';

type Build = {
  app: string;
  repo: string;
  environment: string;
  platform: 'android' | 'ios';
  file: 'APK' | 'AAB' | 'IPA';
  artifactId: number;
  runNumber: number;
  runUrl: string;
  branch: string;
  commit: string;
  createdAt: string;
  expiresAt: string;
  installable: boolean;
  author?: string;
};
type Release = {
  app: string;
  repo: string;
  runNumber: number;
  runUrl: string;
  state: 'ok' | 'fallido' | 'en-curso' | 'cancelado';
  author?: string;
  commit: string;
  createdAt: string;
};
type Data = { configured: boolean; refreshedAt?: string; errors: string[]; builds: Build[]; releases?: Release[] };
type QrLink = { build: Build; url: string; expiresAt: string };

const SOON_MS = 2 * 86_400_000;

const date = (iso: string) => new Date(iso).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });

const RELEASE_STATE = {
  ok: (c: string) => <StatusOK>{c}</StatusOK>,
  fallido: (c: string) => <StatusError>{c}</StatusError>,
  'en-curso': (c: string) => <StatusRunning>{c}</StatusRunning>,
  cancelado: (c: string) => <StatusWarning>{c}</StatusWarning>,
};
const RELEASE_TEXT = { ok: 'OK', fallido: 'falló', 'en-curso': 'compilando', cancelado: 'cancelado' };

// GitHub borra el artefacto en expiresAt; en ámbar cuando quedan menos de 2 días.
const expiry = (b: Build) => {
  const left = Date.parse(b.expiresAt) - Date.now();
  if (left >= SOON_MS) return date(b.expiresAt);
  return <StatusWarning>{date(b.expiresAt)} (caduca pronto)</StatusWarning>;
};

// QR solo fuera de prod (APK e IPA de desarrollo); en prod se instala desde las tiendas.
const noQr = (b: Build) => {
  if (b.environment !== 'prod') return 'QR: falta iosBundleId';
  return b.platform === 'ios' ? 'Se instala desde TestFlight' : 'Se instala desde Google Play (interno)';
};

/** Tabla de builds con QR; con repo, solo las de esa app. */
const MobileBuildsTable = ({ repo }: { repo?: string }) => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<string>();
  const [qr, setQr] = useState<QrLink>();

  useEffect(() => {
    (async () => {
      const res = await fetch(`${await discovery.getBaseUrl('mobile-builds')}/builds`);
      if (!res.ok) throw new Error(`El backend respondió ${res.status}`);
      setData(await res.json());
    })().catch(e => setError(String(e?.message ?? e)));
  }, [discovery, fetch]);

  const link = async (build: Build, download = false) => {
    setError(undefined);
    const res = await fetch(`${await discovery.getBaseUrl('mobile-builds')}/links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ artifactId: build.artifactId, download }),
    });
    if (!res.ok) {
      setError(`No se pudo generar el enlace (${res.status})`);
      return undefined;
    }
    return (await res.json()) as { url: string; expiresAt: string };
  };

  const openQr = async (build: Build) => {
    const l = await link(build);
    if (l) setQr({ build, ...l });
  };

  const download = async (build: Build) => {
    const l = await link(build, true);
    if (l) window.location.assign(l.url);
  };

  const columns: TableColumn<Build>[] = [
    { title: 'App', field: 'app', defaultGroupOrder: 0 },
    { title: 'Ambiente', field: 'environment' },
    { title: 'Plataforma', field: 'platform', render: b => `${b.platform === 'ios' ? 'iOS' : 'Android'} (${b.file})` },
    { title: 'Build', field: 'runNumber', render: b => <Link to={b.runUrl}>#{b.runNumber}</Link> },
    { title: 'Rama', render: b => `${b.branch} (${b.commit})` },
    { title: 'Autor', field: 'author', render: b => b.author ?? '—' },
    { title: 'Fecha', field: 'createdAt', render: b => date(b.createdAt) },
    { title: 'Disponible hasta', field: 'expiresAt', render: expiry },
    {
      title: 'Obtener',
      sorting: false,
      render: b => (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="small" variant="outlined" color="primary" onClick={() => download(b)}>
              Descargar {b.file}
            </Button>
            {b.installable && (
              <Button size="small" variant="outlined" onClick={() => openQr(b)}>
                QR
              </Button>
            )}
          </div>
          <Typography variant="caption" color="textSecondary" component="div">
            {b.installable ? 'Se descarga en .zip' : `${noQr(b)} · se descarga en .zip`} · binario sin Appdome
          </Typography>
        </>
      ),
    },
  ];

  const builds = (data?.builds ?? []).filter(b => !repo || b.repo === repo);
  const releases = (data?.releases ?? []).filter(r => !repo || r.repo === repo);

  return (
    <>
      {error && <WarningPanel title="Builds móviles" message={error} />}
      {data && !data.configured && <WarningPanel title="Sin configurar" message="Falta mobileBuilds en el appConfig de Argo." />}
      {data?.errors.map(e => <WarningPanel key={e} severity="info" title="No se pudo leer una app" message={e} />)}
      {!data && !error && <Progress />}
      {releases.map(r => (
        <Typography key={r.repo} variant="body2" component="div" style={{ marginBottom: 8 }}>
          {RELEASE_STATE[r.state](`${r.app}: último release ${RELEASE_TEXT[r.state]}`)}{' '}
          <Link to={r.runUrl}>#{r.runNumber}</Link> · {r.commit} · {r.author ?? '—'} · {date(r.createdAt)}
        </Typography>
      ))}
      {data && (
        <Table<Build>
          title={data.refreshedAt ? `Actualizado ${date(data.refreshedAt)}` : 'Primera lectura en curso (cada 15 min)'}
          options={{ paging: false, grouping: !repo, search: !repo, padding: 'dense' }}
          columns={repo ? columns.slice(1) : columns}
          data={builds}
          emptyContent={
            <div style={{ padding: 16 }}>
              {repo
                ? `No hay builds vigentes de ${repo} (o el repo no está en mobileBuilds.apps).`
                : 'No hay builds de las ramas release en los últimos 7 días (GitHub borra los artefactos a los 7 días).'}
            </div>
          }
        />
      )}
      <Dialog open={!!qr} onClose={() => setQr(undefined)}>
        {qr && (
          <>
            <DialogTitle>
              {qr.build.app} · {qr.build.environment} · {qr.build.platform === 'ios' ? 'iOS' : 'Android'} #{qr.build.runNumber}
            </DialogTitle>
            <DialogContent style={{ textAlign: 'center' }}>
              <QRCodeSVG value={qr.url} size={240} includeMargin />
              <Typography variant="body2">
                Escanéalo con el teléfono. Caduca a las {new Date(qr.expiresAt).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}.
              </Typography>
              {qr.build.platform === 'ios' && (
                <Typography variant="caption" color="textSecondary" component="p">
                  En iPhone solo instala si el dispositivo está registrado en el perfil de desarrollo.
                </Typography>
              )}
              <Link to={qr.url}>Abrir enlace</Link>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setQr(undefined)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

/** Última build del release de cada app móvil: QR si se puede instalar, descarga si es de tienda. */
export const MobileBuildsPage = () => (
  <Page themeId="tool">
    <Header title="Builds móviles" subtitle="Última build de las ramas release de cada app (APK e IPA): artefactos de GitHub Actions, se guardan 7 días" />
    <Content>
      <MobileBuildsTable />
    </Content>
  </Page>
);

/** Pestaña Builds en la ficha de una app móvil. */
export const MobileBuildsContent = () => {
  const { entity } = useEntity();
  return <MobileBuildsTable repo={entity.metadata.annotations?.[SLUG]} />;
};

export const isMobileApp = (entity: Entity) =>
  entity.spec?.type === 'mobile-app' && Boolean(entity.metadata.annotations?.[SLUG]);
